import React, { Fragment, useEffect, useState } from 'react';

import { useRaidContext } from '../../context/RaidContext';

import GeneralOpportunitiesTable from './GeneralOpportunitiesTable';
import GeneralOpportunityUpsertModal from './GeneralOpportunityUpsertModal';
import TrashedGeneralOpportunitiesTable from './TrashedGeneralOpportunitiesTable';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

export const UPSERT_OPPORTUNITY_MODAL_ID = 'upsert-general-opportunity-modal';

export default function GeneralOpportunitiesIndex({ handleCreated }) {
    const [isLoading, setIsLoading] = useState(false);

    const {
        opportunities,
        trashedOpportunities,
        isMasterSource,
        routes,
        fetchGeneralOpportunities,
        fetchTrashedGeneralOpportunities,
        canEditOpportunities,
        canDeleteOpportunities,
        getQuery,
        selectedOpportunity,
        setSelectedOpportunity,
        toggleModalVisibility,
        pushUrlToOriginal,
        modalsVisibility,
        departmentsOptions,
        loadingDatasets,
    } = useRaidContext();

    const opportunityModal = useModal();

    const toast = useToast();

    const confirm = useConfirmationModal();

    useEffect(() => {
        setIsLoading(true);
        const requests = [fetchGeneralOpportunities()];
        if (isMasterSource) {
            requests.push(fetchTrashedGeneralOpportunities());
        }
        Promise.all(requests)
            .catch((e) => {
                console.error(e);
            })
            .finally(() => setIsLoading(false));
    }, []);

    // `?type=opportunity&id=` opens that record, matching the risk, issue and
    // decision registers. Runs off `opportunities` rather than on mount, since
    // the list it looks the record up in arrives asynchronously.
    useEffect(() => {
        const currentId = getQuery().type === 'opportunity' ? getQuery().id : null;
        if (currentId) {
            const opportunity = opportunities.find((o) => o.id === parseInt(currentId));
            if (opportunity) {
                setSelectedOpportunity(opportunity);
                toggleModalVisibility(UPSERT_OPPORTUNITY_MODAL_ID, true);
            }
        }
    }, [opportunities]);

    useEffect(() => {
        if (modalsVisibility[UPSERT_OPPORTUNITY_MODAL_ID]) {
            opportunityModal.show();
        } else {
            opportunityModal.hide();
        }
    }, [modalsVisibility[UPSERT_OPPORTUNITY_MODAL_ID]]);

    const onEdit = (model) => {
        if (!canEditOpportunities) {
            return;
        }
        setSelectedOpportunity(model);
        toggleModalVisibility(UPSERT_OPPORTUNITY_MODAL_ID, true);
    };

    const onCreated = () => {
        pushUrlToOriginal();
        fetchGeneralOpportunities();

        if (handleCreated && typeof handleCreated === 'function') {
            handleCreated();
        }
    };

    const onClose = () => {
        // Strip `?type=&id=` on the way out, as the risk register does. The
        // deep-link effect above re-runs whenever the list is refetched, so
        // leaving the query string in place would silently reopen this record
        // the next time any opportunity is created or deleted.
        if (getQuery().type) {
            pushUrlToOriginal();
        }
        toggleModalVisibility(UPSERT_OPPORTUNITY_MODAL_ID, false);
    };

    const deleteOpportunity = (model) => {
        const deleteUrl = routes.deleteGeneralOpportunityUrl.replace('GENERAL_OPPORTUNITY_ID', model.id);

        axios
            .delete(deleteUrl)
            .then(({ data }) => {
                fetchGeneralOpportunities();
                if (isMasterSource) {
                    fetchTrashedGeneralOpportunities();
                }

                if (data?.status === 'ok' && data?.message) {
                    toast.success(data.message);
                }
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const onDelete = (model) => {
        if (!canDeleteOpportunities) {
            return;
        }

        confirm({
            title: $t('Delete Opportunity'),
            message: $t(
                'Are you sure you want to delete this opportunity? If this opportunity has a budget impact attached to a budget line it may take a few minutes to be reflected in the department. Alternatively, press the Recalculate Totals button on the Budget Dashboard.',
            ),
            onConfirm: () => {
                deleteOpportunity(model);
            },
        });
    };

    const onRestore = (model) => {
        if (!canDeleteOpportunities) {
            return;
        }

        const restoreUrl = routes.restoreGeneralOpportunityUrl.replace('GENERAL_OPPORTUNITY_ID', model.id);

        axios
            .post(restoreUrl)
            .then(({ data }) => {
                fetchGeneralOpportunities();
                fetchTrashedGeneralOpportunities();

                if (data?.status === 'ok' && data?.message) {
                    toast.success(data.message);
                }
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const onForceDelete = (model) => {
        if (!canDeleteOpportunities) {
            return;
        }

        confirm({
            title: $t('Permanently Delete Opportunity'),
            message: $t('Are you sure you want to permanently delete this opportunity? This action cannot be undone.'),
            onConfirm: () => {
                const forceDeleteUrl = routes.forceDeleteGeneralOpportunityUrl.replace('GENERAL_OPPORTUNITY_ID', model.id);

                axios
                    .delete(forceDeleteUrl)
                    .then(({ data }) => {
                        fetchTrashedGeneralOpportunities();

                        if (data?.status === 'ok' && data?.message) {
                            toast.success(data.message);
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            },
        });
    };

    return (
        <Fragment>
            <div className="table__card tw-space-y-8">
                <div>
                    <h4 className="table__header tw-mb-6">{$t('Opportunities')}</h4>
                    <GeneralOpportunitiesTable
                        isLoading={isLoading}
                        opportunities={opportunities}
                        canEdit={canEditOpportunities}
                        canDelete={canDeleteOpportunities}
                        onEdit={(opportunity) => onEdit(opportunity)}
                        onDelete={(opportunity) => onDelete(opportunity)}
                    />
                </div>
                {canDeleteOpportunities && isMasterSource && (
                    <div>
                        <h4 className="table__header tw-mb-6">{$t('Deleted Opportunities')}</h4>
                        <TrashedGeneralOpportunitiesTable
                            isLoading={isLoading}
                            opportunities={trashedOpportunities}
                            onRestore={(opportunity) => onRestore(opportunity)}
                            onForceDelete={(opportunity) => onForceDelete(opportunity)}
                        />
                    </div>
                )}
            </div>

            <GeneralOpportunityUpsertModal
                id={UPSERT_OPPORTUNITY_MODAL_ID}
                modal={opportunityModal}
                isLoading={loadingDatasets}
                opportunity={selectedOpportunity}
                storeUrl={routes.storeGeneralOpportunityUrl}
                updateUrl={routes.updateGeneralOpportunityUrl}
                departmentsOptions={departmentsOptions}
                fetchBudgetLinesUrl={routes.fetchGeneralRiskBudgetLinesUrl}
                onClose={() => onClose()}
                onCreated={() => onCreated()}
            />
        </Fragment>
    );
}
